<script setup lang="ts">
import {
  List,
  ListItem,
  Navbar,
  Button as VBtn,
} from '@wartek-id/belajar-id-components'

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthModal } from '@/composables/useAuthModal'
import { useGA } from '@/composables'
import { trackSatuEvent } from '@/utilities/satuEvent'

interface DropdownItem {
  text: string
  value: string
  onClick?: (item: DropdownItem) => void
}

const { type, showMenu, show: showModal } = useAuthModal()
const showMenuOnMobile = ref(false)
const isFixed = ref(false)
const store = useStore()
const ga = useGA()

const showModalHandler = (typeVal: string) => {
  if (typeVal === 'pengajuan-akun') {
    type.value = typeVal
    showModal.value = true
  }
  else if (typeVal === 'login-akun') {
    window.location.href = '/profile'
  }
  else {
    window.location.href = import.meta.env.VITE_ADMIN_BASE_URL
  }
}

const items = ref<DropdownItem[]>([
  {
    text: 'Profil Akun belajar.id',
    value: 'login-akun',
    async onClick(item: DropdownItem) {
      try {
        await trackSatuEvent({
          event_name: 'PROFILE_PAGE_ACCESSED',
          page_title: 'Home Page',
        })
      }
      catch (error) {
        console.error('Error tracking profile page access:', error)
      }
      showModalHandler(item.value)
    },
  },
  {
    text: 'Masuk Portal Admin',
    value: 'portal-admin',
    async onClick(item: DropdownItem) {
      try {
        await trackSatuEvent({
          event_name: 'ADMIN_PAGE_ACCESSED',
          page_title: 'Home Page',
        })
      }
      catch (error) {
        console.error('Error tracking admin page access:', error)
      }
      showModalHandler(item.value)
    },
  },
])

const router = useRouter()

const toggleDropdown = () => {
  showMenuOnMobile.value = !showMenuOnMobile.value
  isFixed.value = !!showMenuOnMobile.value
}

const trackButuhBantuan = () => {
  trackSatuEvent({
    event_name: 'CLICKED',
    page_title: 'Home Page',
    attributes: [
      {
        key: 'element_text',
        value: 'Butuh Bantuan',
      },
      {
        key: 'destination_url',
        value: 'https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/categories/52397558782361-Akun-belajar-id',
      },
      {
        key: 'page_location',
        value: 'header',
      },
    ],
  })
  window.open('https://pusatinformasi.rumahpendidikan.kemendikdasmen.go.id/hc/id/categories/52397558782361-Akun-belajar-id', '_blank')
}

const isOpenModalResetPassword = computed({
  get() {
    return store.state.resetPassword.isOpen
  },
  set(val) {
    store.commit('resetPassword/setModal', val)
  },
})
</script>

<template>
  <div data-qa="navbar" class="fixed top-0 left-0 z-10 w-full navbar-container">
    <AnnouncementBar />
    <Navbar class="py-0">
      <div class="px-1 xl:px-0 container-xl mx-auto py-2">
        <div class="flex items-center justify-between">
          <div class="md:flex items-center w-auto hidden">
            <router-link
              :to="{ name: 'home' }"
              class="inline-flex md:justify-start justify-between logo gap-0 items-center"
            >
              <img
                class="w-[46px] h-[45px]"
                src="/images/logo-belajar-id.png"
                alt="Logo Kemdikbudristek RI desktop"
                width="46"
                height="45"
              >
              <span
                class="font-bold leading-[17px] px-2 text-black"
              >
                Akun<br>belajar.id
              </span>
            </router-link>
          </div>
          <div class="md:flex hidden items-center gap-x-6">
            <NavbarDropdown
              label="Akun belajar.id"
              :items="items"
            />
            <VBtn
              variant="primary"
              suffix-icon="ic:outline-contact-support"
              suffix-icon-class="!w-5"
              @click="trackButuhBantuan"
            >
              Butuh Bantuan
            </VBtn>
          </div>
          <div class="flex w-full items-center justify-between md:hidden">
            <router-link
              :to="{ name: 'home' }"
              class="inline-flex md:justify-start justify-between logo gap-0 items-center"
            >
              <img
                class="w-[46px] h-[45px]"
                src="/images/logo-belajar-id.png"
                alt="Logo Kemdikbudristek RI desktop"
                width="46"
                height="45"
              >
              <span class="font-bold leading-[17px] px-2">
                Akun<br>belajar.id
              </span>
            </router-link>
            <VBtn
              fab
              class="border-none"
              aria-label="Menu"
              @click="toggleDropdown"
            >
              <icon name="menu" :width="24" :height="24" />
            </VBtn>
          </div>
        </div>
      </div>
    </Navbar>
    <Transition name="fade">
      <Navbar v-model="showMenuOnMobile" class="md:hidden shadow px-0">
        <div class="lg:container mx-auto px-0 py-2">
          <div class="flex flex-col gap-y-3">
            <VBtn
              class="justify-between !text-black px-0"
              variant="quiet"
              full-width
              suffix-icon="akar-icons:chevron-down"
              suffix-icon-class="!w-3"
              @click="showMenu = !showMenu"
              @blur="showMenu = false"
            >
              Akun belajar.id
            </VBtn>
            <List v-if="showMenu" @mousedown.prevent>
              <ListItem
                v-for="(item, index) in items"
                :key="index"
                class="text-start !text-sm ml-2 mb-2"
                @click="item.onClick?.(item)"
              >
                {{ item.text }}
              </ListItem>
            </List>
            <VBtn
              class="mt-4"
              variant="primary"
              suffix-icon="ic:outline-contact-support"
              suffix-icon-class="!w-5"
              @click="trackButuhBantuan"
            >
              Butuh Bantuan
            </VBtn>
          </div>
        </div>
      </Navbar>
    </Transition>
  </div>

  <ResetPasswordLogin v-model="isOpenModalResetPassword" />
</template>
